import { useState, useMemo } from 'react';
import { Link } from 'react-router';
import { EmptyState } from '@lms/ui';
import {
  Clock,
  Loader2,
  FileText,
  Award,
  Search,
  X,
  CheckCircle2,
  AlertTriangle,
  Lock,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { useEvaluations } from '../hooks/useEvaluations';
import { useEnrollments } from '../hooks/useEnrollments';
import { useAuthStore } from '../store/auth';
import type { Evaluation, EvaluationAttempt } from '@lms/shared/types';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';

type FilterTab = 'ALL' | 'PENDING' | 'COMPLETED';

export function Evaluations() {
  const { user } = useAuthStore();
  const { t } = useLanguage();

  const [activeTab, setActiveTab] = useState<FilterTab>('ALL');
  const [selectedProgramId, setSelectedProgramId] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data: evaluationsData, isLoading, error } = useEvaluations({ userId: user?.id });
  const { data: enrollments } = useEnrollments({ userId: user?.id });

  const rawEvaluations = useMemo(() => {
    return Array.isArray(evaluationsData) ? (evaluationsData as Evaluation[]) : [];
  }, [evaluationsData]);

  // Derive status per evaluation
  const enrichedEvaluations = useMemo(() => {
    return rawEvaluations.map(eval_ => {
      const attempts = (eval_.attempts as EvaluationAttempt[] | undefined) || [];
      const latestAttempt = attempts[0];
      const hasPassed =
        eval_.isRecognizedFromPriorLearning === true || latestAttempt?.isPassed === true;
      const isRemedial =
        latestAttempt &&
        latestAttempt.isPassed === false &&
        (latestAttempt.score ?? 0) < eval_.passingScore;
      const isAttempted = attempts.length > 0;

      let statusKey: 'PASSED' | 'REMEDIAL' | 'NOT_ATTEMPTED' = 'NOT_ATTEMPTED';
      if (hasPassed) statusKey = 'PASSED';
      else if (isRemedial) statusKey = 'REMEDIAL';

      return {
        ...eval_,
        latestAttempt,
        statusKey,
        hasPassed,
        isRemedial,
        isAttempted,
      };
    });
  }, [rawEvaluations]);

  // Filtered List
  const filteredEvaluations = useMemo(() => {
    return enrichedEvaluations.filter(eval_ => {
      // 1. Tab filter
      if (activeTab === 'PENDING' && eval_.hasPassed) return false;
      if (activeTab === 'COMPLETED' && !eval_.hasPassed) return false;

      // 2. Program filter
      if (selectedProgramId !== 'ALL') {
        const progId =
          eval_.programId ||
          eval_.flow?.programId ||
          eval_.level?.programId ||
          eval_.material?.flow?.programId;
        if (progId !== selectedProgramId) return false;
      }

      // 3. Search query
      if (searchQuery.trim().length > 0) {
        const q = searchQuery.toLowerCase();
        const titleMatch = eval_.title.toLowerCase().includes(q);
        const descMatch = eval_.description?.toLowerCase().includes(q) || false;
        const progName = eval_.program?.name?.toLowerCase() || '';
        const progMatch = progName.includes(q);
        if (!titleMatch && !descMatch && !progMatch) return false;
      }

      return true;
    });
  }, [enrichedEvaluations, activeTab, selectedProgramId, searchQuery]);

  // Statistics counters
  const totalCount = enrichedEvaluations.length;
  const pendingCount = enrichedEvaluations.filter(e => !e.hasPassed).length;
  const completedCount = enrichedEvaluations.filter(e => e.hasPassed).length;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-sm text-on-surface-variant font-body">{t(copy.evaluations.loading)}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-container text-on-error-container p-6 rounded-2xl animate-fadeIn">
        <h3 className="font-headline font-bold mb-2">{t(copy.evaluations.errorTitle)}</h3>
        <p className="text-sm font-body">{(error as Error)?.message || t(copy.common.error)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn pb-16">
      {/* Editorial Header */}
      <section className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 pb-6 border-b border-outline-variant/10">
        <div className="space-y-1 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-[10px] font-headline font-bold uppercase tracking-wider mb-1">
            <Sparkles size={12} />
            <span>Pusat Evaluasi Akademik</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-headline font-extrabold text-on-surface tracking-tight m-0">
            <Bilingual {...copy.evaluations.title} />
          </h1>
          <p className="text-sm sm:text-base font-body text-on-surface-variant leading-relaxed m-0">
            <Bilingual {...copy.evaluations.subtitle} />
          </p>
        </div>

        {/* Quick Stats Pill */}
        <div className="flex items-center gap-2 bg-surface-container-low px-4 py-2 rounded-2xl border border-outline-variant/10 shrink-0">
          <div className="text-center px-2">
            <div className="text-xs font-bold text-on-surface-variant uppercase">Total</div>
            <div className="text-base font-headline font-bold text-on-surface">{totalCount}</div>
          </div>
          <div className="h-6 w-px bg-outline-variant/20" />
          <div className="text-center px-2">
            <div className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase">
              Tertunda
            </div>
            <div className="text-base font-headline font-bold text-amber-700 dark:text-amber-400">
              {pendingCount}
            </div>
          </div>
          <div className="h-6 w-px bg-outline-variant/20" />
          <div className="text-center px-2">
            <div className="text-xs font-bold text-success uppercase">Selesai</div>
            <div className="text-base font-headline font-bold text-success">{completedCount}</div>
          </div>
        </div>
      </section>

      {/* Filter and Search Controls */}
      <div className="space-y-4">
        {/* Segmented Filter Tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-outline-variant/10 pb-3">
          <button
            type="button"
            onClick={() => setActiveTab('ALL')}
            className={`px-4 py-2 rounded-xl text-xs font-headline font-bold transition-all cursor-pointer ${
              activeTab === 'ALL'
                ? 'bg-primary text-white shadow-level1'
                : 'bg-surface-container-low text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Bilingual {...copy.evaluations.filterAll} /> ({totalCount})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('PENDING')}
            className={`px-4 py-2 rounded-xl text-xs font-headline font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'PENDING'
                ? 'bg-amber-600 text-white shadow-level1'
                : 'bg-surface-container-low text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Clock size={14} />
            <Bilingual {...copy.evaluations.filterPending} />
            {pendingCount > 0 && (
              <span className="ml-1 px-1.5 py-0.2 rounded-full bg-amber-500/20 text-amber-900 dark:text-amber-200 text-[10px]">
                {pendingCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('COMPLETED')}
            className={`px-4 py-2 rounded-xl text-xs font-headline font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'COMPLETED'
                ? 'bg-success text-white shadow-level1'
                : 'bg-surface-container-low text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <CheckCircle2 size={14} />
            <Bilingual {...copy.evaluations.filterCompleted} /> ({completedCount})
          </button>
        </div>

        {/* Toolbar: Program selector & Search input */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-40 rtl:left-auto rtl:right-4"
            />
            <input
              type="text"
              placeholder={t(copy.evaluations.searchPlaceholder)}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-surface-container-low border-none rounded-xl pl-11 pr-10 rtl:pl-10 rtl:pr-11 py-2.5 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all h-10"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-on-surface-variant hover:text-primary rounded-full"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Program filter dropdown */}
          {enrollments && enrollments.length > 1 && (
            <div className="shrink-0">
              <select
                value={selectedProgramId}
                onChange={e => setSelectedProgramId(e.target.value)}
                className="bg-surface-container-low rounded-xl px-4 py-2 text-xs font-body text-on-surface border-none focus:ring-2 focus:ring-primary/20 outline-none h-10"
              >
                <option value="ALL">{t(copy.evaluations.allPrograms)}</option>
                {enrollments.map(enr => (
                  <option key={enr.programId} value={enr.programId}>
                    {enr.program.name} ({enr.program.code})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Evaluations Cards List */}
      {filteredEvaluations.length === 0 ? (
        <EmptyState
          icon={Clock}
          title={t(copy.evaluations.noEvaluationsTitle)}
          description={t(copy.evaluations.noEvaluationsDesc)}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredEvaluations.map(eval_ => {
            const hasPassed = eval_.hasPassed;
            const isLocked = Boolean(eval_.isLocked && !hasPassed);
            const isRemedial = eval_.isRemedial;
            const score = eval_.latestAttempt?.score;
            const progId =
              eval_.programId ||
              eval_.flow?.programId ||
              eval_.level?.programId ||
              eval_.material?.flow?.programId;
            const enrolledProg = enrollments?.find(e => e.programId === progId)?.program;
            const programName =
              eval_.program?.name || enrolledProg?.name || t(copy.evaluations.generalAssessments);

            return (
              <div
                key={eval_.id}
                className={`p-6 rounded-[2rem] bg-surface-container-lowest border transition-all duration-300 flex flex-col justify-between gap-4 shadow-level1 ${
                  hasPassed
                    ? 'border-success/20 hover:border-success/40'
                    : isRemedial
                      ? 'border-amber-500/30 hover:border-amber-500/60'
                      : 'border-outline-variant/10 hover:border-primary/30'
                }`}
              >
                <div className="space-y-3">
                  {/* Top Tags & Status Chip */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-[10px] font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-lg truncate max-w-[200px]">
                      {programName}
                    </span>

                    {/* Status Badge */}
                    {isLocked ? (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-headline font-bold bg-surface-container-high text-on-surface-variant flex items-center gap-1">
                        <Lock size={12} />
                        <span>Terkunci</span>
                      </span>
                    ) : hasPassed ? (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-headline font-bold bg-success/15 text-success flex items-center gap-1">
                        <CheckCircle2 size={12} />
                        <span>
                          {t(copy.evaluations.statusPassed)}{' '}
                          {score !== undefined && score !== null ? `(${score}%)` : ''}
                        </span>
                      </span>
                    ) : isRemedial ? (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-headline font-bold bg-amber-500/15 text-amber-700 dark:text-amber-400 flex items-center gap-1">
                        <AlertTriangle size={12} />
                        <span>
                          {t(copy.evaluations.statusRemedial)}{' '}
                          {score !== undefined && score !== null ? `(${score}%)` : ''}
                        </span>
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-headline font-bold bg-primary/10 text-primary flex items-center gap-1">
                        <Clock size={12} />
                        <span>{t(copy.evaluations.statusNotAttempted)}</span>
                      </span>
                    )}
                  </div>

                  {/* Title & Description */}
                  <div>
                    <h3 className="font-headline font-bold text-base text-on-surface line-clamp-2 m-0 group-hover:text-primary transition-colors">
                      {eval_.title}
                    </h3>
                    <p className="text-xs text-on-surface-variant font-body line-clamp-2 mt-1.5 m-0 leading-relaxed">
                      {eval_.description || t(copy.evaluations.noDescription)}
                    </p>
                  </div>

                  {/* Metadata Row */}
                  <div className="flex flex-wrap items-center gap-4 text-[11px] font-mono text-on-surface-variant pt-1 border-t border-outline-variant/10">
                    <div className="flex items-center gap-1.5">
                      <Award size={14} className="text-secondary" />
                      <span>
                        {t(copy.evaluations.passMark)}: {eval_.passingScore}%
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Clock size={14} className="text-primary" />
                      <span>
                        {eval_.timeLimit
                          ? `${eval_.timeLimit} ${t(copy.evaluations.minutes)}`
                          : t(copy.evaluations.noLimit)}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <FileText size={14} className="text-on-surface-variant" />
                      <span className="capitalize">
                        {eval_.type === 'EXAM'
                          ? t(copy.evaluations.exam)
                          : eval_.type === 'SURVEY'
                            ? t(copy.evaluations.survey)
                            : t(copy.evaluations.quiz)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bottom Action Footer */}
                <div className="pt-3 border-t border-outline-variant/10 flex items-center justify-between gap-2">
                  <div className="text-[10px] text-on-surface-variant font-medium">
                    {eval_.maxAttempts && eval_.maxAttempts > 0
                      ? `${t(copy.evaluations.attempts)}: ${eval_.maxAttempts}x`
                      : t(copy.evaluations.infinite)}
                  </div>

                  {isLocked ? (
                    <span className="inline-flex items-center gap-1 px-4 py-2 rounded-xl bg-surface-container-high text-on-surface-variant text-xs font-bold opacity-60">
                      <Lock size={13} />
                      <span>Terkunci</span>
                    </span>
                  ) : (
                    <Link
                      to={`/evaluations/${eval_.id}`}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-white font-bold text-xs shadow-xs transition-all no-underline ${
                        hasPassed
                          ? 'bg-primary hover:opacity-90'
                          : isRemedial
                            ? 'bg-amber-600 hover:bg-amber-700'
                            : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}
                    >
                      <span>
                        {hasPassed
                          ? t(copy.evaluations.viewResultBtn)
                          : isRemedial
                            ? t(copy.evaluations.retakeExamBtn)
                            : t(copy.evaluations.takeExamBtn)}
                      </span>
                      <ArrowRight size={13} />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
